/**
 * Proximity inventory overlay — a translucent, borderless floating glyph label
 * shown while a dead guard is adjacent to the player. No pickup interaction yet;
 * purely a "what's here" readout.
 *
 * Positioned at a FIXED pixel offset from the player's on-screen point (the
 * caller passes the player's screen position, computed with the renderer's real
 * transform), so it sits in the same spot around the player at any zoom.
 */

import type { Guard } from '../sim/guard';
import { findAdjacentLoot } from '../sim/inventory';

export class InventoryOverlay {
  readonly root: HTMLElement;

  constructor(host: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'inv-overlay';
    this.root.style.display = 'none';
    host.appendChild(this.root);
  }

  /**
   * @param screenX,screenY  the player's on-screen pixel point (the overlay
   *   floats a fixed offset from it — no zoom scaling).
   */
  update(screenX: number, screenY: number, playerX: number, playerY: number, guards: readonly Guard[]): void {
    const nearby = findAdjacentLoot(playerX, playerY, guards);
    if (nearby.length === 0) {
      this.root.style.display = 'none';
      return;
    }
    // Show loot from the first corpse (usually only one adjacent). Multi-
    // corpse case just picks one; a full inventory browser is out of scope.
    const c = nearby[0]!;
    this.root.style.display = 'block';
    this.root.style.left = `${screenX}px`;
    this.root.style.top = `${screenY}px`;
    this.root.innerHTML =
      `<div class="inv-title">corpse · #${c.guardId}</div>` +
      c.items
        .map(
          (it) =>
            `<div class="inv-item"><span class="inv-glyph" style="color:#${it.color.toString(16).padStart(6, '0')}">${it.glyph}</span> ${it.label}</div>`,
        )
        .join('');
  }
}
